// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — the room's view of the presenter's Khipu (the Khipu plan, player
 * parity, 2026-10-08): while a meeting's presenter plays a Khipu, everyone
 * following sees its title, the step's title and captions, and the step's
 * pins (as a line here; the host puts them on the globe). It is read-only:
 * the presenter moves the Khipu on, and the room follows.
 *
 * The card wears the lesson panel's frame, a side panel on a desktop
 * (floatingPanel.ts: it slides in and the globe steps aside), folds to its
 * head, and goes when the presenter closes the Khipu or the room
 * stops following. A phone in AR adopts it like the Khipu player
 * (`xrPhoneChrome`).
 */
import { t } from '../i18n'
import type { RoomNote } from '../services/roomProtocol'
import type { VrKhipuState } from '../services/vrKhipuPanel'
import { escapeAttr, escapeHtml } from './domUtils'
import { announceSidePanel, leaveSidePanel } from './floatingPanel'

export const ROOM_NOTE_ID = 'room-note'

let shown: RoomNote | null = null
let folded = false

/** Show the presenter's note, or take it away (null). Redraws only when it changed. */
export function showRoomNote(note: RoomNote | null): void {
  if (JSON.stringify(note) === JSON.stringify(shown)) return
  shown = note
  let card = document.getElementById(ROOM_NOTE_ID)
  if (!note) {
    if (card) leaveSidePanel(card)
    folded = false
    return
  }
  if (!card) {
    card = document.createElement('section')
    card.id = ROOM_NOTE_ID
    card.className = 'lesson-player khipu-player room-note'
    card.setAttribute('aria-label', t('room.note.aria'))
    card.addEventListener('click', (e) => {
      if (!(e.target as HTMLElement).closest('.lesson-fold')) return
      folded = !folded
      const current = shown
      shown = null
      if (current) showRoomNote(current)
      document.querySelector<HTMLElement>(`#${ROOM_NOTE_ID} .lesson-fold`)?.focus()
    })
    document.body.appendChild(card)
    announceSidePanel()
  }
  const pins = note.pins.map((p) => p.note).filter(Boolean)
  card.classList.toggle('folded', folded)
  card.innerHTML = `
    <div class="lesson-head room-note-head">
      <div class="lesson-head-text">
        <span class="lesson-title">${escapeHtml(note.title)}</span>
        <span class="lesson-progress">${escapeHtml(t('room.note.kind'))}</span>
      </div>
      <button type="button" class="lesson-icon lesson-fold" aria-expanded="${String(!folded)}" aria-label="${escapeAttr(t(folded ? 'khipu.player.expand' : 'khipu.player.collapse'))}">${folded ? '&#x25BE;' : '&#x25B4;'}</button>
    </div>
    <div class="lesson-body"${folded ? ' hidden' : ''} aria-live="polite">
      ${note.step ? `<h3 class="lesson-step-title">${escapeHtml(note.step)}</h3>` : ''}
      ${note.lines.length ? `<div class="khipu-captions">${note.lines.map((l) => `<p class="khipu-caption">${escapeHtml(l)}</p>`).join('')}</div>` : ''}
      ${pins.length ? `<p class="lesson-notice">${escapeHtml(t('room.note.pins', { list: pins.join(' · ') }))}</p>` : ''}
    </div>`
}

/** The note shown, if any (the headset draws it too). */
export function shownRoomNote(): RoomNote | null {
  return shown
}

let vrCache: { note: RoomNote; state: VrKhipuState } | null = null

/** The headset's Khipu panel for a note: the same object until the note changes. */
export function roomNoteVrState(note: RoomNote | null): VrKhipuState | null {
  if (!note) return null
  if (vrCache?.note === note) return vrCache.state
  const pins = note.pins.map((p) => p.note).filter(Boolean)
  const state: VrKhipuState = {
    title: note.title,
    progress: t('vr.khipu.following'),
    heading: note.step,
    dataset: '',
    lines: [...note.lines, ...(pins.length ? [t('room.note.pins', { list: pins.join(' · ') })] : [])],
    media: 0,
    canPrev: false,
    isLast: false,
    offersPlay: false,
    playing: false,
    readOnly: true,
  }
  vrCache = { note, state }
  return state
}
